import { AdminPageHeader } from '@/components/patterns/admin-page'
import { AppUpdateForm } from '@/components/app-updates/AppUpdateForm'
import ImageLightboxGallery from '@/components/media/ImageLightboxGallery'
import ImageLightboxPreviewTrigger, {
  createLightboxPreviewItem,
} from '@/components/media/ImageLightboxPreviewTrigger'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { RichTextContent } from '@/components/ui/rich-text-content'
import { Typography } from '@/components/ui/typography'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useOrganizationContext } from '@/hooks/useOrganizationContext'
import { adminRouteTitleHead } from '@/lib/admin-route-title'
import { useAbility } from '@/lib/casl'
import dayjs from '@/lib/dayjs'
import {
  useMarkAppUpdateReadMutation,
  useMarkAppUpdatesViewedMutation,
  appUpdatesQueries,
  useDeleteAppUpdateMutation,
  useAppUpdate,
} from '@/lib/services/app-updates/app-updates.query'
import { appUpdateParamsSchema } from '@/lib/services/app-updates/app-updates.schema'
import {
  canDeleteAppUpdates,
  canEditAppUpdates,
  canViewUnpublishedAppUpdates,
  getScribeEmbedSrc,
  isGlobalOrganizationContext,
  resolveAppUpdateDestinationUrl,
  shouldAutoMarkAppUpdateRead,
} from '@/lib/services/app-updates/app-updates.utils'
import { cn, getErrorMessage } from '@/lib/utils'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import {
  ArrowLeft,
  CalendarDays,
  CircleCheck,
  ExternalLink,
  Pencil,
  Share2,
  Sparkles,
  Trash2,
} from 'lucide-react'
import { useEffect, useEffectEvent, useRef, useState } from 'react'
import { toast } from 'sonner'

export const Route = createFileRoute('/_authenticated/$orgId/whats-new/$appUpdateId')({
  component: WhatsNewDetailPage,
  head: adminRouteTitleHead,
  params: {
    parse: (params) => appUpdateParamsSchema.parse(params),
  },
  loader: async ({ context, params }) => {
    const appUpdate = await context.queryClient.ensureQueryData(
      appUpdatesQueries.show({
        organizationId: params.orgId,
        appUpdateId: params.appUpdateId,
      })
    )

    return {
      appUpdate: appUpdate?.data ?? appUpdate,
      isReadOnly: !canEditAppUpdates(context.ability),
    }
  },
})

function formatDate(value) {
  if (!value) {
    return 'Today'
  }

  return dayjs.utc(value).format('MMM D, YYYY')
}

function AppUpdateBadges({ appUpdate, showAudience, showPublishingState }) {
  return (
    <>
      <Badge variant={appUpdate.is_read ? 'muted' : 'brand'}>
        {appUpdate.is_read ? 'Read' : 'Unread'}
      </Badge>
      <Badge variant="secondary">{appUpdate.update_type_label}</Badge>
      {showAudience ? (
        <Badge variant={appUpdate.audience === 'global' ? 'warning' : 'success'}>
          {appUpdate.audience_label}
        </Badge>
      ) : null}
      {showPublishingState && !appUpdate.is_published ? (
        <Badge variant="warning">Unpublished</Badge>
      ) : null}
    </>
  )
}

function IconActionButton({ label, children, className, ...props }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          size="icon"
          aria-label={label}
          className={cn('size-10', className)}
          {...props}
        >
          {children}
          <span className="sr-only">{label}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent sideOffset={6}>{label}</TooltipContent>
    </Tooltip>
  )
}

function getAppUpdateBenefits(appUpdate) {
  return Array.isArray(appUpdate?.benefits)
    ? appUpdate.benefits.map((benefit) => String(benefit || '').trim()).filter(Boolean)
    : []
}

function getBenefitTitle(appUpdate) {
  return appUpdate?.audience === 'global' ? 'Platform Benefit' : 'Florist Benefit'
}

function getAppUpdateGalleryPreviewEntries(appUpdate) {
  const gallery = Array.isArray(appUpdate?.gallery) ? appUpdate.gallery : []

  return gallery
    .map((media) => {
      const alt = media?.name || appUpdate?.title || 'App update screengrab'
      const caption = media?.name || appUpdate?.title || ''
      const previewItem = createLightboxPreviewItem({
        media,
        alt,
        caption,
      })

      return previewItem
        ? {
            media,
            alt,
            caption,
            previewItem,
          }
        : null
    })
    .filter(Boolean)
}

export function AppUpdateGallerySection({ appUpdate }) {
  const previewEntries = getAppUpdateGalleryPreviewEntries(appUpdate)

  if (previewEntries.length === 0) {
    return null
  }

  const previewItems = previewEntries.map((entry) => entry.previewItem)

  return (
    <section className="space-y-4">
      <div>
        <Typography variant="h3" className="pb-1">
          Screengrab Highlights
        </Typography>
        <Typography variant="supporting" className="block">
          Visual details from this update.
        </Typography>
      </div>
      <ImageLightboxGallery items={previewItems} className="contents">
        {({ getTriggerProps }) => (
          <div className="grid gap-4 md:grid-cols-3">
            {previewEntries.map((entry, index) => (
              <ImageLightboxPreviewTrigger
                key={entry.previewItem.id ?? `${entry.media.url}-${index}`}
                media={entry.media}
                alt={entry.alt}
                caption={entry.caption}
                triggerProps={getTriggerProps(index)}
                frameClassName="aspect-[4/3] w-full rounded-lg border bg-muted/30"
                imageClassName="object-cover"
              />
            ))}
          </div>
        )}
      </ImageLightboxGallery>
    </section>
  )
}

export function WhatsNewDetailPage() {
  const { orgId, appUpdateId } = Route.useParams()
  const loaderData = Route.useLoaderData()
  const [isEditing, setIsEditing] = useState(false)
  const { data: appUpdateResponse } = useAppUpdate({ appUpdateId })
  const appUpdate = appUpdateResponse?.data ?? appUpdateResponse ?? loaderData.appUpdate
  const ability = useAbility()
  const { isImpersonating, organization } = useOrganizationContext()
  const isGlobalContext = isGlobalOrganizationContext(organization)
  const canEdit = isGlobalContext && canEditAppUpdates(ability)
  const canDelete = isGlobalContext && canDeleteAppUpdates(ability)
  const canViewPublishingState = isGlobalContext && canViewUnpublishedAppUpdates(ability)
  const canViewAudienceTag = ability.can('access', 'global') && isGlobalContext
  const deleteMutation = useDeleteAppUpdateMutation()
  const markViewedMutation = useMarkAppUpdatesViewedMutation()
  const markReadMutation = useMarkAppUpdateReadMutation()
  const markedSeenRef = useRef(null)
  const navigate = useNavigate({ from: Route.fullPath })
  const scribeEmbedSrc = getScribeEmbedSrc(appUpdate?.scribe_url)
  const destinationUrl = resolveAppUpdateDestinationUrl(appUpdate?.destination_url, {
    orgId,
  })
  const benefits = getAppUpdateBenefits(appUpdate)
  const markAppUpdateSeen = useEffectEvent((id, { markRead = true } = {}) => {
    // Detail views clear the new badge; read state stays tied to this update.
    markViewedMutation.mutate()
    if (markRead) {
      markReadMutation.mutate(id)
    }
  })

  useEffect(() => {
    if (!orgId || !appUpdate?.id) {
      return
    }

    const seenKey = orgId + ':' + appUpdate.id

    if (markedSeenRef.current === seenKey) {
      return
    }

    markedSeenRef.current = seenKey
    markAppUpdateSeen(appUpdate.id, {
      markRead: shouldAutoMarkAppUpdateRead({ isImpersonating }),
    })
  }, [isImpersonating, orgId, appUpdate?.id])

  const handleDelete = async () => {
    if (!confirm('Delete "' + appUpdate.title + '"?')) {
      return
    }

    try {
      await deleteMutation.mutateAsync(appUpdate.id)
      toast.success('App update deleted')
      await navigate({
        to: '/$orgId/whats-new',
        params: { orgId },
        ignoreBlocker: true,
      })
    } catch (error) {
      toast.error('Failed to delete app update', {
        description: getErrorMessage(error),
      })
    }
  }

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      toast.success('App update link copied to your clipboard')
    } catch {
      toast.error('Could not copy the app update link')
    }
  }

  if (!appUpdate) {
    return null
  }

  if (isEditing && canEdit) {
    return (
      <div className="max-w-6xl space-y-5">
        <AdminPageHeader
          icon={Sparkles}
          title="Edit App Update"
          description="Update the announcement and its supporting media."
          actions={
            <Button type="button" variant="outline" onClick={() => setIsEditing(false)}>
              Cancel
            </Button>
          }
        />
        <AppUpdateForm
          key={appUpdate.id}
          mode="edit"
          appUpdate={appUpdate}
          onSaved={() => setIsEditing(false)}
        />
      </div>
    )
  }

  return (
    <div className="max-w-6xl space-y-6">
      <Button variant="ghost" asChild className="-ml-2">
        <Link to="/$orgId/whats-new" params={{ orgId }}>
          <ArrowLeft className="size-4" />
          Back to What's New
        </Link>
      </Button>

      <section className="space-y-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div className="flex flex-wrap items-center gap-3">
            <AppUpdateBadges
              appUpdate={appUpdate}
              showAudience={canViewAudienceTag}
              showPublishingState={canViewPublishingState}
            />
            <span className="inline-flex items-center gap-1.5 text-sm text-muted-foreground">
              <CalendarDays className="size-4" />
              {formatDate(appUpdate.update_date)}
            </span>
          </div>
          <div className="flex flex-wrap gap-2 lg:justify-end">
            {destinationUrl ? (
              <Button asChild>
                <a href={destinationUrl} target="_blank" rel="noreferrer">
                  View in App
                  <ExternalLink className="size-4" />
                </a>
              </Button>
            ) : null}
            <IconActionButton label="Share" variant="outline" onClick={handleShare}>
              <Share2 className="size-4" />
            </IconActionButton>
            {canEdit ? (
              <IconActionButton label="Edit" variant="outline" onClick={() => setIsEditing(true)}>
                <Pencil className="size-4" />
              </IconActionButton>
            ) : null}
            {canDelete ? (
              <IconActionButton
                label="Delete"
                variant="outline"
                className="text-destructive hover:text-destructive"
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
              >
                <Trash2 className="size-4" />
              </IconActionButton>
            ) : null}
          </div>
        </div>

        <Typography variant="h1" className="w-full pb-0">
          {appUpdate.title}
        </Typography>

        {!destinationUrl && appUpdate.destination_url ? (
          <Typography variant="supporting" className="block">
            This destination URL needs more context before it can open here.
          </Typography>
        ) : null}
      </section>

      {scribeEmbedSrc ? (
        <Card className="overflow-hidden">
          <CardContent className="p-0">
            <iframe
              title={appUpdate.title + ' Scribe video'}
              src={scribeEmbedSrc}
              width="100%"
              height="800"
              allow="fullscreen"
              className="w-full border-0"
              style={{ aspectRatio: '16 / 12', minHeight: 480 }}
              allowFullScreen
            />
          </CardContent>
        </Card>
      ) : null}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,2fr)_minmax(18rem,1fr)] xl:items-start">
        <Card>
          <CardHeader>
            <CardTitle>What's New</CardTitle>
          </CardHeader>
          <CardContent>
            <RichTextContent
              html={appUpdate.description_html}
              fallbackText={appUpdate.description}
              className="text-base leading-7"
            />
          </CardContent>
        </Card>

        {benefits.length > 0 ? (
          <Card>
            <CardHeader>
              <CardTitle>{getBenefitTitle(appUpdate)}</CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-3">
                {benefits.map((benefit) => (
                  <li key={benefit} className="flex gap-3 text-sm leading-6">
                    <CircleCheck className="mt-0.5 size-4 shrink-0 text-interaction" />
                    <span>{benefit}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ) : null}
      </div>

      <AppUpdateGallerySection appUpdate={appUpdate} />
    </div>
  )
}
